"use client";
import React from 'react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "~/components/ui/carousel";
import GlowingButton from "~/components/reusables/glowing-button";
import { LinkTrackers } from "~/components/reusables/link-trackers";
import SmartImage from "~/components/reusables/SmartImage";

const dummyResources = [
  {
    id: 1,
    eyebrow: "On-demand whitepaper",
    title: "Join us to see how the Internet of Cognition works in practice.",
    description: "Register for a guided walkthrough to learn how distributed superintelligence unlocks better business outcomes.",
    image: "/images/pages/internet-of-cognition/CREA-1454.png",
    ctaText: "Register today",
    ctaLink: "https://event.on24.com/wcc/r/5253740/DE3C3561BDB98EBCDE2B5A8A56BF0356?utm_campaign=fy26q3_quantum_ww_owned-media_aop-webinar12_website&utm_channel=website",
    target: "_blank"
  },
  {
    id: 2,
    eyebrow: "Whitepaper",
    title: "The Internet of Cognition is our blueprint for scaling out intelligence, together.",
    description: "Agentic AI stands at an inflection point: powerful, yet siloed. Read the full paper on the Internet of Cognition to learn how we’re expanding the power of multi-agent systems.",
    image: "/images/pages/internet-of-cognition/CREA-1412.png",
    ctaText: "Read whitepaper",
    ctaLink: "/internet-of-cognition/whitepaper",
    target: "_self"
  }
];

export function ResourcesSection() {
  const hasMoreThanTwo = dummyResources.length > 2;

  const Card = ({ resource }: { resource: typeof dummyResources[0] }) => (
    <div 
      className="flex flex-col bg-[#00142B]/40 rounded-lg border border-[#003D82]/40 p-6 md:p-8 shrink-0 overflow-hidden w-full max-w-[613px] h-full" 
    >
      <div className="flex-1 flex flex-col h-full">
        {/* Eyebrow: 16px, 500 */}
        <span className="text-[16px] font-[500] text-white mb-4 md:mb-6 uppercase tracking-wider">
          {resource.eyebrow}
        </span>
        
        {/* Paragraph 1: 24px, 350 */}
        <p className="text-[24px] font-[350] text-white leading-snug mb-6 pr-0 md:pr-4">
          {resource.title}
        </p>

        {/* Paragraph 2: 18px, 350 with left border */}
        <div className="border-l-2 border-[#294a82] pl-4 md:pl-5 mb-8">
          <p className="text-[18px] font-[350] text-[#c9c9c9] leading-relaxed">
            {resource.description}
          </p>
        </div>
        
        <div className="mt-auto flex flex-col items-start w-full">
          {/* Image */}
          <div className="w-full max-w-[398px] mb-6">
             <img src={resource.image} alt={resource.title} className="w-full h-auto object-contain" />
          </div>

          <GlowingButton className="w-max">
            <LinkTrackers
              href={resource.ctaLink}
              target={resource.target || "_self"}
              segmentMsg="Resource CTA clicked"
              segmentOpt={{
                link_label: resource.ctaText,
                location: "resources_section",
              }}
              className="font-btn-lg"
            >
              {resource.ctaText}
            </LinkTrackers>
          </GlowingButton>
        </div>
      </div>
    </div>
  );

  return (
    <section className="relative z-10 py-16 md:py-24 text-white" style={{ backgroundColor: "#050511" }}>
      <div className="container">
        <div className="max-w-[1258px] mx-auto w-full">
        <div className="mb-12">
          {/* Section title: 42px, weight 350 */}
          <h2 className="text-[42px] font-[350] leading-tight">
            Resources
          </h2>
        </div>

        <Carousel
          opts={{
            align: "start",
            loop: false,
            dragFree: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-4 md:-ml-8 items-stretch">
            {dummyResources.map((resource) => (
              <CarouselItem key={resource.id} className="pl-4 md:pl-8 basis-full md:basis-1/2 lg:basis-auto flex">
                <Card resource={resource} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className={`flex items-center gap-4 mt-8 ${hasMoreThanTwo ? '' : 'md:hidden'}`}>
            <CarouselPrevious className="static translate-y-0 translate-x-0" />
            <CarouselNext className="static translate-y-0 translate-x-0" />
          </div>
        </Carousel>
        </div>
      </div>
    </section>
  );
}
